import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

import projectAnalysisService from '../../services/projectAnalysisService';
import { useAnalysisRunsStore } from '../../stores/analysisRunsStore';
import { isRunActive, type AnalysisRun } from '../../types/projectAnalysis';
import { phaseLabel } from '../Projects/analysis/analysisFormat';
import { formatDateTime, timeAgo } from '../Projects/projectFormat';

const LIMIT = 6;

/**
 * Dashboard widget « Analyses »: the project analyses in flight (live, from the
 * runs store the dock polls) and the latest finished ones (plan §9.5).
 */
export const AnalysisRunsWidget: React.FC = () => {
  const { t } = useTranslation();
  const live = useAnalysisRunsStore((s) => s.runs);
  const activeCount = live.filter(isRunActive).length;
  const [recent, setRecent] = useState<AnalysisRun[] | null>(null);

  // Reload the history when a run starts or ends.
  useEffect(() => {
    let cancelled = false;
    projectAnalysisService
      .listRuns()
      .then((list) => !cancelled && setRecent(list))
      .catch(() => !cancelled && setRecent([]));
    return () => {
      cancelled = true;
    };
  }, [activeCount]);

  const runs = useMemo(() => {
    const byId = new Map<string, AnalysisRun>();
    for (const r of recent ?? []) byId.set(r.id, r);
    for (const r of live) byId.set(r.id, r);
    return [...byId.values()]
      .sort((a, b) => Number(isRunActive(b)) - Number(isRunActive(a)) || b.created_at.localeCompare(a.created_at))
      .slice(0, LIMIT);
  }, [recent, live]);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="mb-4 text-lg font-semibold text-foreground">{t('projects.widgets.analysisRuns')}</h3>
      {recent === null && runs.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('projects.loading')}
        </p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('projects.widgets.analysisRunsEmpty')}</p>
      ) : (
        <ul className="divide-y divide-border">
          {runs.map((run) => {
            const active = isRunActive(run);
            const when = run.finished_at ?? run.started_at ?? run.created_at;
            return (
              <li key={run.id}>
                <Link
                  to={`/projets/${run.project_id}/analyse`}
                  className="flex items-center gap-3 rounded-md py-2.5 hover:bg-muted/40"
                >
                  <span className="shrink-0">
                    {active ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
                    ) : run.status === 'succeeded' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" aria-hidden />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {run.project_title || t('projects.runs.untitled')}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground" title={formatDateTime(when)}>
                      {active
                        ? phaseLabel(run, t)
                        : [
                            run.status === 'succeeded' ? t('projects.runs.succeeded') : t('projects.runs.failed'),
                            timeAgo(when),
                          ].join(' · ')}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
